import React, { useEffect, useMemo, useState } from "react";
import { api, toastError } from "../../lib/api";
import { PageHeader, EmptyState } from "../../components/domain/Bits";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Lightbulb, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { useEffectiveRole } from "../../lib/roles";
import { hasActionAccess } from "../../lib/workspaces";

type SuggestionRow = {
  id: string; suggestion_number: string; anonymity: "named" | "anonymous"; category: string;
  title: string; suggestion: string; why_it_matters?: string; proposed_solution?: string;
  status: string; management_response?: string; publish_to_team: boolean;
  submitter_name?: string; created_at: string; updated_at: string; version: number;
};

const CATEGORIES = [
  ["process", "Process"],
  ["system", "System / Tools"],
  ["client_patient_experience", "Client / Patient Experience"],
  ["training", "Training"],
  ["sales", "Sales"],
  ["safety_compliance", "Safety / Compliance"],
  ["cost_saving", "Cost Saving"],
  ["workplace", "Workplace"],
  ["other", "Other"],
] as const;
const STATUSES = ["new", "under_review", "planned", "implemented", "not_planned", "duplicate"];
const pretty = (value?: string) => (value || "—").replace(/_/g, " ");

export default function SuggestionBox() {
  const { user } = useAuth();
  const role = useEffectiveRole();
  const management = ["owner_admin", "admin_support", "manager_trainer"].includes(String(role));
  const canAdd = hasActionAccess(user, "suggestions", "add");
  const canEdit = management && hasActionAccess(user, "suggestions", "edit");

  const [rows, setRows] = useState<SuggestionRow[]>([]);
  const [open, setOpen] = useState(false);
  const [reviewing, setReviewing] = useState<SuggestionRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [form, setForm] = useState({
    anonymity: "named",
    category: "process",
    title: "",
    suggestion: "",
    why_it_matters: "",
    proposed_solution: "",
  });
  const [review, setReview] = useState({ status: "new", management_response: "", publish_to_team: false });

  const load = async () => {
    try {
      const { data } = await api.get("/suggestions", {
        params: management ? { q: q.trim(), status, category } : {},
      });
      setRows(data.items || []);
    } catch (e) { toastError(e); }
  };
  useEffect(() => { void load(); }, [management, status, category]);

  const submit = async () => {
    if (!canAdd || saving) return;
    setSaving(true);
    try {
      const { data } = await api.post("/suggestions", {
        ...form,
        why_it_matters: form.why_it_matters.trim() || null,
        proposed_solution: form.proposed_solution.trim() || null,
      });
      toast.success(
        form.anonymity === "anonymous"
          ? `Anonymous suggestion submitted as ${data.suggestion_number}. It is not linked to your account.`
          : `Suggestion submitted as ${data.suggestion_number}.`
      );
      setOpen(false);
      setForm({ anonymity: "named", category: "process", title: "", suggestion: "", why_it_matters: "", proposed_solution: "" });
      await load();
    } catch (e) { toastError(e); } finally { setSaving(false); }
  };

  const beginReview = (row: SuggestionRow) => {
    setReviewing(row);
    setReview({
      status: row.status || "new",
      management_response: row.management_response || "",
      publish_to_team: Boolean(row.publish_to_team),
    });
  };

  const saveReview = async () => {
    if (!reviewing || !canEdit || saving) return;
    setSaving(true);
    try {
      const { data } = await api.patch("/suggestions/" + reviewing.id, {
        version: reviewing.version,
        ...review,
        management_response: review.management_response.trim() || null,
      });
      toast.success("Suggestion review updated.");
      setReviewing(data);
      await load();
    } catch (e) { toastError(e); } finally { setSaving(false); }
  };

  const totalPublished = useMemo(() => rows.filter((row) => row.publish_to_team).length, [rows]);
  const valid = form.title.trim().length >= 3 && form.suggestion.trim().length >= 3;

  return <>
    <PageHeader
      title={management ? "Suggestion Box & Improvement Ideas" : "Suggestion Box"}
      subtitle={management
        ? "Review improvement ideas without mixing them into complaints or disciplinary records"
        : "Share an idea to improve how the team works"}
      actions={canAdd ? <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild><Button className="rounded-full" data-testid="new-suggestion"><Plus className="mr-1 h-4 w-4" /> New suggestion</Button></DialogTrigger>
        <DialogContent className="max-h-[92dvh] max-w-2xl overflow-auto">
          <DialogHeader><DialogTitle>Share a suggestion</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Submission type</Label><Select value={form.anonymity} onValueChange={(v) => setForm({ ...form, anonymity: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="named">Named</SelectItem><SelectItem value="anonymous">Anonymous to management</SelectItem></SelectContent></Select>
              {form.anonymity === "anonymous" && <p className="mt-2 rounded bg-amber-50 p-3 text-xs text-amber-900">Your user ID, name, IP address, and request reference are not stored with this suggestion. Because it is not linked to your account, private follow-up cannot be delivered unless management publishes an update to the team.</p>}
            </div>
            <div><Label>Category</Label><Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{CATEGORIES.map(([v,l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Suggestion title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div><Label>Your suggestion</Label><Textarea className="min-h-28" value={form.suggestion} onChange={(e) => setForm({ ...form, suggestion: e.target.value })} /></div>
            <div><Label>Why does this matter? <span className="text-slate-400">(optional)</span></Label><Textarea value={form.why_it_matters} onChange={(e) => setForm({ ...form, why_it_matters: e.target.value })} /></div>
            <div><Label>Proposed solution <span className="text-slate-400">(optional)</span></Label><Textarea value={form.proposed_solution} onChange={(e) => setForm({ ...form, proposed_solution: e.target.value })} /></div>
          </div>
          <DialogFooter><Button onClick={submit} disabled={!valid || saving}>{saving ? "Submitting…" : "Submit suggestion"}</Button></DialogFooter>
        </DialogContent>
      </Dialog> : undefined}
    />

    <div className="mx-auto max-w-7xl space-y-5 p-6 md:p-8">
      <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-4 text-sm text-emerald-900">
        <strong>Suggestion Box is separate from Complaints & Cases.</strong> Improvement ideas are not employee-relations or disciplinary records.
      </div>

      {management && <div className="grid gap-3 rounded-lg border bg-white p-4 md:grid-cols-4">
        <div className="md:col-span-2"><Label>Search</Label><div className="flex gap-2"><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Reference, title, or suggestion" onKeyDown={(e) => { if (e.key === "Enter") void load(); }} /><Button variant="outline" onClick={() => void load()}><Search className="h-4 w-4" /></Button></div></div>
        <div><Label>Status</Label><Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem>{STATUSES.map((v) => <SelectItem key={v} value={v}>{pretty(v)}</SelectItem>)}</SelectContent></Select></div>
        <div><Label>Category</Label><Select value={category || "all"} onValueChange={(v) => setCategory(v === "all" ? "" : v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem>{CATEGORIES.map(([v,l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></div>
      </div>}

      {management && <div className="text-xs text-slate-500">Published team updates visible in this result set: {totalPublished}</div>}

      {rows.length === 0 ? <EmptyState icon={Lightbulb} title={management ? "No suggestions found" : "No suggestion updates yet"} /> :
        <div className="space-y-3">{rows.map((row) => <article key={row.id} className="rounded-lg border bg-white p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div><div className="text-xs uppercase text-slate-500">{row.suggestion_number} · {pretty(row.category)} · {pretty(row.status)}</div><h2 className="mt-1 font-semibold">{row.title}</h2></div>
            {management && <Button size="sm" variant="outline" onClick={() => beginReview(row)}>Review</Button>}
          </div>
          {management && <div className="mt-2 text-xs text-slate-500">Submitted by: {row.anonymity === "anonymous" ? "Anonymous" : row.submitter_name || "Named team member"}</div>}
          <p className="mt-3 whitespace-pre-wrap text-sm text-slate-700">{row.suggestion}</p>
          {row.why_it_matters && <div className="mt-3 rounded bg-slate-50 p-3 text-sm"><strong>Why it matters:</strong> {row.why_it_matters}</div>}
          {row.proposed_solution && <div className="mt-2 rounded bg-slate-50 p-3 text-sm"><strong>Proposed solution:</strong> {row.proposed_solution}</div>}
          {row.management_response && (management || row.publish_to_team) && <div className="mt-3 rounded bg-sky-50 p-3 text-sm"><strong>Management response:</strong> {row.management_response}{row.publish_to_team && <div className="mt-1 text-xs text-sky-700">Published to the team</div>}</div>}
        </article>)}</div>}
    </div>

    {reviewing && management && <Dialog open onOpenChange={(value) => { if (!value) setReviewing(null); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>{reviewing.suggestion_number} · Review suggestion</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="rounded bg-slate-50 p-3 text-sm"><div className="font-medium">{reviewing.title}</div><div className="mt-2 whitespace-pre-wrap">{reviewing.suggestion}</div></div>
          <div><Label>Status</Label><Select value={review.status} onValueChange={(v) => setReview({ ...review, status: v })} disabled={!canEdit}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{STATUSES.map((v) => <SelectItem key={v} value={v}>{pretty(v)}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Management response</Label><Textarea value={review.management_response} onChange={(e) => setReview({ ...review, management_response: e.target.value })} disabled={!canEdit} placeholder="Explain the decision, planned action, or outcome." /></div>
          <label className="flex items-start gap-2 text-sm"><input type="checkbox" checked={review.publish_to_team} onChange={(e) => setReview({ ...review, publish_to_team: e.target.checked })} disabled={!canEdit} /><span><strong>Publish this update to the team.</strong><br /><span className="text-xs text-slate-500">Useful for anonymous suggestions and improvements everyone should know about. A management response is required before publishing.</span></span></label>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setReviewing(null)}>Close</Button>{canEdit && <Button onClick={saveReview} disabled={saving}>{saving ? "Saving…" : "Save review"}</Button>}</DialogFooter>
      </DialogContent>
    </Dialog>}
  </>;
}
